import React, { useState, useEffect, useRef } from 'react';
import { useUser, useAuth, UserButton } from '@clerk/clerk-react';
import './ChatInterface.css';
import Sidebar from './Sidebar';
import MessageArea from './MessageArea';
import InputBox from './InputBox';
import { redirectToCheckout, redirectToCustomerPortal } from '../stripe/StripeService';
import config from '../../config';

// Make sure FontAwesome is loaded
// The <link> should be in public/index.html

const ChatInterface = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [freeMessages, setFreeMessages] = useState(0);
  const [chatId, setChatId] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [thinkingModel, setThinkingModel] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [fileFormat, setFileFormat] = useState('step');
  const [userProduct, setUserProduct] = useState(null);
  const [showFloatingMessage, setShowFloatingMessage] = useState(true);
  const [isBlocked, setIsBlocked] = useState(false);

  const chatboxRef = useRef(null);
  const backendAuthenticatedRef = useRef(false); // New ref to track backend authentication status

  // Helper function to create headers with authentication
  const createAuthHeaders = async () => {
    const token = await getToken();
    if (!token) {
      console.error('No authentication token available');
      throw new Error('Authentication required');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  };
  
  // Helper function to ensure backend authentication is established
  const ensureAuthenticated = async (force = false) => {
    if (!force && backendAuthenticatedRef.current) {
      console.log('Backend already authenticated, skipping explicit check.');
      return true;
    }
    try {
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        return false;
      }
      
      console.log('Ensuring backend authentication is established...');
      const response = await fetch(`/api/clerk/authorize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('Backend authentication verified successfully');
        backendAuthenticatedRef.current = true; // Set ref to true on success
        return true;
      } else {
        console.error('Failed to authenticate with backend:', await response.text());
        backendAuthenticatedRef.current = false; // Set ref to false on failure
        return false;
      }
    } catch (error) {
      console.error('Error ensuring authentication:', error);
      backendAuthenticatedRef.current = false; // Set ref to false on error
      return false;
    }
  };

  // Initialize chat session
  useEffect(() => {
    const initializeChat = async () => {
      try {
        console.log('Initializing chat session...');
        
        // Make sure we're authenticated first with both Clerk and our backend
        const token = await getToken();
        if (!token) {
          console.error('No authentication token available');
          return;
        }
        
        // Ensure backend authentication is established first
        const isAuthenticated = await ensureAuthenticated(true); // Force check on initial load
        if (!isAuthenticated) {
          console.error('Backend authentication failed');
          window.location.href = '/login';
          return;
        }
        
        // Initialize the chat session with authentication - use simple relative path
        const response = await fetch(`/init_chat`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('Init chat response:', data);
          
          if (data.status === 'success') {
            setChatId(data.chat_id);
            setFreeMessages(data.free_messages || 0);
            setChatHistory(data.chat_history.map(id => ({ id })) || []);
            console.log('Chat session initialized with ID:', data.chat_id);
            
            // If system prompt is provided, initialize messages with it
            if (data.system_prompt) {
              setMessages([data.system_prompt]);
              console.log('Initialized messages with system prompt');
            }

            // Fetch user data and check subscription status
            fetchUserData();
            checkSubscriptionStatus();
            checkUserLimitStatus();
          } else {
            console.error('Failed to initialize chat session:', data);
          }
        } else {
          console.error('Error initializing chat session:', await response.text());
          
          // Check if session expired
          if (response.status === 401) {
            console.error('Authentication failed. Redirecting to login...');
            window.location.href = '/login'; 
          }
        }
      } catch (error) {
        console.error('Error initializing chat session:', error);
      }
    };

    // Only initialize if we have user data
    if (user) {
      initializeChat();
    }
  }, [user, getToken]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    if (!sidebarOpen) {
      setShowFloatingMessage(false);
    }
  };

  const dismissFloatingMessage = () => {
    setShowFloatingMessage(false);
  };

  const startNewChat = async () => {
    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        return;
      }
      
      // Ensure backend authentication is established first
      const isAuthenticated = await ensureAuthenticated(); // No force, will use ref
      if (!isAuthenticated) {
        console.error('Backend authentication failed');
        window.location.href = '/login';
        return;
      }

      // Call the backend to clear the current chat
      const clearResponse = await fetch(`/clear_chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (clearResponse.ok) {
        // Initialize a new chat session
        const initResponse = await fetch(`/init_chat`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        // Check if we need to redirect to login
        if (initResponse.status === 401) {
          console.log('Authentication required. Redirecting to login...');
          window.location.href = '/sign-in';
          return;
        }

        if (initResponse.ok) {
          const data = await initResponse.json();

          if (data.status === 'success') {
            // Initialize with system prompt if provided, otherwise empty array
            if (data.system_prompt) {
              setMessages([data.system_prompt]);
              console.log('Initialized new chat with system prompt');
            } else {
              setMessages([]);
            }
            
            setChatId(data.chat_id);
            setFreeMessages(data.free_messages || 0);

            // Update chat history
            if (data.chat_history) {
              setChatHistory(data.chat_history.map(id => ({ id })));
            }

            console.log('Started new chat with ID:', data.chat_id);
          } else {
            console.error('Failed to initialize new chat:', data);
          }
        } else {
          console.error('Error initializing new chat:', await initResponse.text());
        }
      } else {
        console.error('Error clearing chat:', await clearResponse.text());
      }
    } catch (error) {
      console.error('Error starting new chat:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        return;
      }
      
      // Make sure we're authenticated with the backend before accessing protected endpoints
      // We don't need to check the result here as this is called after ensureAuthenticated in initializeChat
      
      console.log('Fetching user data...');
      const response = await fetch(`/api/user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('User data:', userData);

        setFreeMessages(userData.free_messages || 0);
        setUserProduct(userData.product);
      } else {
        console.error('Error fetching user data:', await response.text());
        
        // If we get a 401, try to re-authenticate and retry
        if (response.status === 401) {
          console.log('Authentication expired, attempting to re-authenticate...');
          const isAuthenticated = await ensureAuthenticated();
          
          if (isAuthenticated) {
            // Retry the fetch after re-authentication
            console.log('Re-authenticated successfully, retrying user data fetch');
            const retryResponse = await fetch(`/api/user`, {
              headers: {
                'Authorization': `Bearer ${await getToken()}`
              }
            });
            
            if (retryResponse.ok) {
              const userData = await retryResponse.json();
              console.log('User data retrieved on retry:', userData);
              setFreeMessages(userData.free_messages || 0);
              setUserProduct(userData.product);
            } else {
              console.error('Failed to fetch user data after re-authentication:', await retryResponse.text());
            }
          } else {
            console.error('Re-authentication failed, redirecting to login');
            window.location.href = '/login';
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const checkUserLimitStatus = async () => {
    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      console.log('Checking user limit status...');
      const response = await fetch(`/get_free_messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('User limit status:', data);
        setFreeMessages(data.free_messages || 0);
        setIsBlocked(data.is_blocked || false);
        if (data.product) {
          setUserProduct(data.product);
        }
      } else {
        console.error('Error checking user limit status:', await response.text());
      }
    } catch (error) {
      console.error('Error checking user limit status:', error);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      console.log('Checking subscription status with Stripe...');
      const response = await fetch(`/api/stripe/check-subscription-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const statusData = await response.json();
        console.log('Subscription status check result:', statusData);

        if (statusData.product) {
          // Update the UI with the synced product status
          setUserProduct(statusData.product);
          console.log(`User product status synced: ${statusData.product}`);
        }
      } else {
        console.error('Error checking subscription status:', await response.text());
        
        // If we get a 401, try to re-authenticate and retry
        if (response.status === 401) {
          console.log('Authentication expired, attempting to re-authenticate...');
          const isAuthenticated = await ensureAuthenticated();
          
          if (isAuthenticated) {
            // Retry the subscription check after re-authentication
            console.log('Re-authenticated successfully, retrying subscription check');
            const retryResponse = await fetch(`/api/stripe/check-subscription-status`, {
              headers: {
                'Authorization': `Bearer ${await getToken()}`
              }
            });
            
            if (retryResponse.ok) {
              const statusData = await retryResponse.json();
              console.log('Subscription status retrieved on retry:', statusData);
              if (statusData.product) {
                setUserProduct(statusData.product);
              }
            } else {
              console.error('Failed to check subscription after re-authentication:', await retryResponse.text());
            }
          }
        }
      }
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const handleModelToggle = async () => {
    // If user product is null or free, redirect to Stripe Checkout
    if (userProduct === null || userProduct === 'free') {
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        return;
      }

      console.log('Redirecting to Stripe Checkout as product status is:', userProduct);
      // Redirect to Stripe Checkout
      await redirectToCheckout(token);
      return;
    }

    console.log('User has premium product, toggling thinking model');
    // Otherwise toggle the model (only for premium users)
    setThinkingModel(!thinkingModel);
  };

  const handleManageSubscription = async () => {
    const token = await getToken();
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    // Redirect to Stripe Customer Portal
    await redirectToCustomerPortal(token);
  };

  const handleUpgradeSubscription = async () => {
    const token = await getToken();
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    // Redirect to Stripe Checkout
    await redirectToCheckout(token);
  };

  const loadChatHistory = async (historyId) => {
    setLoading(true);

    try {
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        setLoading(false);
        return;
      }
      
      // Ensure backend authentication is established first
      const isAuthenticated = await ensureAuthenticated(); // No force, will use ref
      if (!isAuthenticated) {
        console.error('Backend authentication failed');
        window.location.href = '/login';
        setLoading(false);
        return;
      }

      console.log(`Loading chat history for ID: ${historyId}`);
      const response = await fetch(`/reload_chat/${historyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Chat history loaded successfully:', data);

          // Check if we have messages with 3D models
          const processedMessages = data.messages.map(message => {
            // If the message has stlFile and objectId properties, it has a 3D model
            if (message.stlFile && message.objectId) {
              console.log(`Found 3D model in message with objectId: ${message.objectId}`);
              // Add logic to handle fail.stl for reloaded messages
              if (message.stlFile === '/static/stl/fail.stl') {
                message.stlFile = 'static/stl/fail.stl'; // Adjust path for local display
              }
            }
            return message;
          });

          setMessages(processedMessages);
          setChatId(historyId);
        } else {
          console.error('Failed to reload chat:', data.error);
        }
      } else {
        console.error('Error loading chat history:', await response.text());
        
        // If we get a 401, try to re-authenticate and retry
        if (response.status === 401) {
          console.log('Authentication expired, attempting to re-authenticate...');
          const isReAuthenticated = await ensureAuthenticated();
          
          if (isReAuthenticated) {
            // Retry loading chat history after re-authentication
            console.log('Re-authenticated successfully, retrying chat history load');
            const retryResponse = await fetch(`/reload_chat/${historyId}`, {
              headers: {
                'Authorization': `Bearer ${await getToken()}`
              }
            });
            
            if (retryResponse.ok) {
              const data = await retryResponse.json();
              if (data.success) {
                console.log('Chat history loaded successfully on retry:', data);
                const processedMessages = data.messages.map(message => {
                  if (message.stlFile && message.objectId) {
                    console.log(`Found 3D model in message with objectId: ${message.objectId}`);
                    // Add logic to handle fail.stl for reloaded messages
                    if (message.stlFile === '/static/stl/fail.stl') {
                      message.stlFile = 'static/stl/fail.stl'; // Adjust path for local display
                    }
                  }
                  return message;
                });
                setMessages(processedMessages);
                setChatId(historyId);
              }
            } else {
              console.error('Failed to load chat history after re-authentication:', await retryResponse.text());
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim()) return;
    
    // Add user message to the UI
    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    
    try {
      console.log('Sending message with chatId:', chatId);
      
      // Get authentication token
      const token = await getToken();
      if (!token) {
        console.error('No authentication token available');
        setLoading(false);
        return;
      }
      
      // Ensure backend authentication is established first
      const isAuthenticated = await ensureAuthenticated(); // No force, will use ref
      if (!isAuthenticated) {
        console.error('Backend authentication failed');
        setLoading(false);
        window.location.href = '/login';
        return;
      }
      
      // Get model based on toggle state
      const model = thinkingModel ? "large" : "mini";
      
      // Send message and current message history to backend
      const currentMessages = messages.map(msg => ({ role: msg.role, content: msg.content }));
      
      const response = await fetch(`/get?msg=${encodeURIComponent(message)}&model=${model}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          messages: currentMessages 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        
        // Check if we have a valid STL file
        let stlToDisplay = null;
        if (data.stl_file) {
          if (data.stl_file.startsWith("Error:") || data.stl_file === "static/stl/fail.stl") {
            stlToDisplay = "static/stl/fail.stl"; // Display fail.stl on error or explicit fail.stl
          } else {
            stlToDisplay = data.stl_file; // Display the actual STL file
          }
        }
        
        // Add bot response to messages
        const botMessage = { 
          role: 'assistant', 
          content: data.message,
          stlFile: stlToDisplay,
          stepFile: stlToDisplay ? data.step_file : null,
          objectId: data.object_id,
          generationTime: data.generation_time
        };
        
        setMessages(prev => [...prev, botMessage]);
        
        // If we got a new object ID from a successful generation, update our state
        if (data.object_id && data.object_id !== "0") {
          console.log('Generated object with ID:', data.object_id);
        }
        
        // Update free messages count and check if user is blocked
        const freeMessagesResponse = await fetch(`/get_free_messages`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (freeMessagesResponse.ok) {
          const freeMessagesData = await freeMessagesResponse.json();
          setFreeMessages(freeMessagesData.free_messages || 0);
          setIsBlocked(freeMessagesData.is_blocked || false);
          if (freeMessagesData.product) {
            setUserProduct(freeMessagesData.product);
          }
        }
      } else {
        // If we get a 403 (message limit exceeded), redirect to Stripe checkout
        if (response.status === 403) {
          try {
            const errorData = await response.json();
            console.error('Error response from server:', errorData);
            if (errorData.requires_premium) {
              console.log('Message limit exceeded, redirecting to Stripe checkout');
              await redirectToCheckout(token);
              return;
            }
          } catch (e) {
            console.error('Error parsing 403 response:', e);
            console.error('Error response from server:', await response.text());
          }
        } else {
          console.error('Error response from server:', await response.text());
        }
        
        // If we get a 401, try to re-authenticate and retry
        if (response.status === 401) {
          console.log('Authentication expired, attempting to re-authenticate...');
          const isReAuthenticated = await ensureAuthenticated();
          
          if (isReAuthenticated) {
            // Retry sending the message after re-authentication
            console.log('Re-authenticated successfully, retrying message send');
            const retryResponse = await fetch(`/get?msg=${encodeURIComponent(message)}&model=${model}`, {
              headers: {
                'Authorization': `Bearer ${await getToken()}`
              }
            });
            
            if (retryResponse.ok) {
              const data = await retryResponse.json();
              
              
              // Check if we have a valid STL file
              let stlToDisplay = null;
              if (data.stl_file) {
                if (data.stl_file.startsWith("Error:") || data.stl_file === "static/stl/fail.stl") {
                  stlToDisplay = "static/stl/fail.stl"; // Display fail.stl on error or explicit fail.stl
                } else {
                  stlToDisplay = data.stl_file; // Display the actual STL file
                }
              }
              
              // Add bot response to messages
              const botMessage = { 
                role: 'assistant', 
                content: data.message,
                stlFile: stlToDisplay,
                stepFile: stlToDisplay ? data.step_file : null,
                objectId: data.object_id,
                generationTime: data.generation_time
              };
              
              setMessages(prev => [...prev, botMessage]);
            } else {
              console.error('Failed to send message after re-authentication:', await retryResponse.text());
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chat-container">
      <div className="chat-header">
        <button
          className={`toggle-btn hamburger-icon ${sidebarOpen ? 'open' : ''}`}
          onClick={toggleSidebar}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className="logout-container">
          <UserButton />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        userName={user?.firstName || user?.username || 'User'}
        freeMessages={freeMessages}
        chatId={chatId}
        onNewChat={startNewChat}
        toggleModel={handleModelToggle}
        isThinkingModel={thinkingModel}
        fileFormat={fileFormat}
        setFileFormat={setFileFormat}
        chatHistory={chatHistory}
        onSelectChat={loadChatHistory}
        userProduct={userProduct}
        onManageSubscription={handleManageSubscription}
        onUpgradeSubscription={handleUpgradeSubscription}
      />

      {showFloatingMessage && !sidebarOpen && (
        <div className="floating-message">
          <span>Try our new thinking model!</span>
          <button onClick={dismissFloatingMessage}>OK</button>
        </div>
      )}

      <div className="chat-content">
        <MessageArea
          messages={messages}
          loading={loading}
          fileFormat={fileFormat}
          ref={chatboxRef}
        />
        <InputBox 
          onSendMessage={sendMessage} 
          disabled={loading} 
          isBlocked={isBlocked}
          onUpgrade={handleUpgradeSubscription}
        />
      </div>
    </div>
  );
};

export default ChatInterface;
